//PuzzlePage.tsx
import React from 'react';
import { useParams } from 'react-router-dom';
import ChessboardComponent from './chessboard';
import { Typography, Container, Tabs, Tab, Button, Box, Stack, Paper, CircularProgress } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { usePuzzle } from './hooks/usePuzzle';

interface PuzzlePageProps {
  gameType?: 'endgame' | 'opening';
}

const PuzzlePage: React.FC<PuzzlePageProps> = ({ gameType }) => {
  const { date, difficulty } = useParams<{ date: string; difficulty: 'easy' | 'medium' | 'hard' }>();

  const {
    puzzle,
    loading,
    selectedDate,
    selectedDifficulty,
    changeDate,
    changeDifficulty,
    onPuzzleSolved
  } = usePuzzle(date, difficulty);

  const getSolvedColor = (diff: 'easy' | 'medium' | 'hard') => {
    const solvedStatus = puzzle[diff]?.solved;
    // Darker Green for better contrast on white/light backgrounds
    return solvedStatus === 'yes' ? '#2E7D32' : solvedStatus === 'no' ? '#D32F2F' : '#455A64';
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Chess Puzzle',
        text: 'Check out this chess puzzle I am solving!',
        url: window.location.href
      })
        .then(() => console.log('Successful share'))
        .catch((error) => console.error('Error sharing', error));
    } else {
      navigator.clipboard.writeText(window.location.href)
        .then(() => alert('Puzzle URL copied to clipboard!'))
        .catch(err => console.error('Could not copy text: ', err));
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'var(--tg-theme-secondary-bg-color, #F0F2F5)', py: 0.5 }}>
      <Container maxWidth="md">
        <Stack spacing={1} alignItems="center">

          {/* 1. Compact Header: Date Navigation + Spacer + Difficulty Tabs */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              maxWidth: 600,
              mb: 0.5,
              gap: 0
            }}
          >
            {/* Top: Compact Date Nav */}
            <Box display="flex" alignItems="center" bgcolor="transparent" sx={{ width: '100%', justifyContent: 'center', py: 0.5 }}>
              <Button size="small" onClick={() => { if (selectedDate) { const d = new Date(selectedDate); d.setDate(d.getDate() - 1); changeDate(d); } }} disabled={!selectedDate || selectedDate <= new Date('2024-01-01')} sx={{ color: '#375643', minWidth: 24, p: 0 }}>
                <ChevronLeftIcon fontSize="large" />
              </Button>
              <Typography variant="subtitle1" sx={{ mx: 2, fontWeight: 600, fontSize: '0.9rem', color: '#546e7a', letterSpacing: '0.02em', minWidth: '120px', textAlign: 'center' }}>
                {selectedDate ? selectedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
              </Typography>
              <Button size="small" onClick={() => { if (selectedDate) { const d = new Date(selectedDate); d.setDate(d.getDate() + 1); changeDate(d); } }} disabled={!selectedDate || selectedDate >= new Date(new Date().setHours(0, 0, 0, 0))} sx={{ color: '#375643', minWidth: 24, p: 0 }}>
                <ChevronRightIcon fontSize="large" />
              </Button>
            </Box>

            {/* Bottom: Full Width Difficulty Tabs */}
            <Box sx={{ width: '100%' }}>
              <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, height: 32, display: 'flex', alignItems: 'center', backgroundColor: '#fff', width: '100%', overflow: 'hidden' }}>
                <Tabs
                  value={selectedDifficulty}
                  onChange={(_, val) => changeDifficulty(val)}
                  textColor="inherit"
                  indicatorColor="primary"
                  variant="fullWidth"
                  sx={{ minHeight: 32, height: 32, width: '100%' }}
                >
                  {['easy', 'medium', 'hard'].map((diff) => {
                    const isSolved = puzzle[diff as 'easy' | 'medium' | 'hard']?.solved === 'yes';
                    return (
                      <Tab
                        key={diff}
                        label={
                          <Stack direction="row" alignItems="center" spacing={0} justifyContent="center" sx={{ width: '100%' }}>
                            {isSolved && <CheckCircleIcon sx={{ fontSize: '1.5rem' }} />}
                            <span>{diff.charAt(0).toUpperCase() + diff.slice(1)}</span>
                          </Stack>
                        }
                        value={diff}
                        sx={{
                          minHeight: 32,
                          height: 32,
                          minWidth: 0,
                          fontWeight: selectedDifficulty === diff ? 'bold' : 'normal',
                          color: getSolvedColor(diff as 'easy' | 'medium' | 'hard'),
                          '&.Mui-selected': { color: getSolvedColor(diff as 'easy' | 'medium' | 'hard') },
                          textTransform: 'none',
                          px: { xs: 0.5, sm: 2 },
                          fontSize: { xs: '0.65rem', sm: '0.875rem' },
                          flex: 1
                        }}
                      />
                    );
                  })}
                </Tabs>
              </Paper>
            </Box>
          </Box>

          {/* 2. Chessboard (Hero) */}
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', position: 'relative' }}>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto', transition: 'opacity 0.3s ease' }}>
              <ChessboardComponent
                fen={puzzle[selectedDifficulty].fen}
                difficulty={selectedDifficulty}
                puzzleId={`puzzle-${(selectedDate || new Date()).toISOString().split('T')[0]}`}
                result={puzzle[selectedDifficulty].result}
                firstMove={puzzle[selectedDifficulty].first_move}
                onPuzzleSolved={() => onPuzzleSolved(selectedDifficulty)}
                onShare={handleShare}
                onNextDifficulty={
                  ['easy', 'medium'].includes(selectedDifficulty)
                    ? () => changeDifficulty(selectedDifficulty === 'easy' ? 'medium' : 'hard')
                    : undefined
                }
              />
            </Box>
            {loading && (
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 20 }}>
                <CircularProgress size={60} sx={{ color: '#375643' }} />
              </Box>
            )}
          </Box>

        </Stack>
      </Container>
    </Box>
  );
};

export default PuzzlePage;
